<!--
  PaginaProjeto.vue — Página pública do projeto ("Saiba Mais" / Doe Seu IR)
  ==========================================================================
  Port para Vue 2 (Options API) do componente entregue pelo design (AUCR-799).
  Apresentacional: recebe a prop `projeto` (ver projetoContrato.helper.js) e
  expõe o slot #simulador. NÃO traz header próprio — a Nav vem do wrapper
  views/web/project/Project.vue. Os botões de doar emitem o evento `doar`
  (o container dispara o fluxo de doação existente).
-->
<template>
<div class="pagina-projeto" :style="themeVars">

  <!-- HERO -->
  <section class="hero" id="top" :style="heroStyle">
    <div class="wrap hero-inner">
      <div>
        <span class="hero-tag">Projeto Incentivado · {{ lei.rotulo }}</span>
        <h1>{{ projeto.nome }}</h1>
        <div class="hero-sub rich" v-html="projeto.descricao"></div>
        <div class="hero-chips">
          <span class="chip" v-if="projeto.tagsCausa && projeto.tagsCausa.length">{{ projeto.tagsCausa[0] }}</span>
          <span class="chip" v-if="projeto.tagsPublico && projeto.tagsPublico.length">{{ projeto.tagsPublico[0] }}</span>
          <span class="chip" v-if="local"><i class="fal fa-map-marker-alt"></i> {{ local }}</span>
        </div>
        <div class="hero-cta">
          <a class="btn btn-lime" href="#simulador" @click.prevent="ga('Descobrir potencial - Hero'); scrollToSimulador()">Descobrir potencial de doação</a>
        </div>
      </div>
      <div class="hero-photo">
        <span class="blob accentblob"></span><span class="blob ring"></span>
        <div class="frame"><img :src="projeto.fotoCapaUrl" :alt="projeto.nome"></div>
        <div class="floattag" v-if="projeto.qtdBeneficiarios">
          <span class="ic"><i class="fas fa-heart"></i></span>
          <span><b>{{ fmtNum(projeto.qtdBeneficiarios) }}</b><span>pessoas impactadas</span></span>
        </div>
        <div class="logo-card" v-if="projeto.instituicao.logoUrl">
          <span class="lc-label">Realização</span>
          <img :src="projeto.instituicao.logoUrl" :alt="projeto.instituicao.razaoSocial">
        </div>
      </div>
    </div>
    <div class="curve"><svg viewBox="0 0 1440 80" preserveAspectRatio="none"><path d="M0,52 C360,82 1080,0 1440,38 L1440,80 L0,80 Z" fill="#F4F9F9"/></svg></div>
  </section>

  <!-- CARD DE DOAÇÃO -->
  <div class="wrap donate-wrap" id="doar">
    <div class="donate-card">
      <div>
        <span class="dc-status">
          <span class="dot"></span> {{ projeto.captacaoAberta ? 'Projeto aberto para receber doações' : 'Doações encerradas' }}
        </span>
        <div class="dc-raised">{{ brl(projeto.valorCaptado) }}</div>
        <div class="dc-goal">captados de <b>{{ brl(projeto.valorAprovado) }}</b> aprovados para o projeto</div>
        <div class="progress"><i :style="{ width: pctCaptacao + '%' }"></i></div>
        <div class="dc-meta"><span>{{ pctCaptacao }}% da meta</span><span>Meta total de captação</span></div>
      </div>
      <div class="dc-right">
        <div class="dc-highlight">
          <b>até {{ lei.pctPF }}%</b>
          <span>Destine até {{ lei.pctPF }}% do seu Imposto de Renda</span>
        </div>
        <a class="btn btn-primary" href="#doar" @click.prevent="emitDoar('Doar com IR - Card')" v-if="projeto.captacaoAberta">Doar com meu Imposto de Renda</a>
        <a class="btn btn-soft btn-sm" href="#simulador" @click.prevent="ga('Descobrir potencial - Card'); scrollToSimulador()">Descobrir potencial de doação</a>
        <p class="dc-note">O valor que iria para o governo vai direto para este projeto.</p>
      </div>
    </div>
  </div>

  <!-- COMO FUNCIONA (% por lei) -->
  <section class="pad" id="como">
    <div class="wrap">
      <div class="section-head center">
        <h2>Como funciona a sua doação</h2>
        <p>Pela declaração completa do IR, você pode destinar até {{ lei.pctPF }}% do imposto devido a este projeto — sem pagar nada a mais. É o mesmo valor, com um destino que você escolhe.</p>
      </div>
      <div class="steps">
        <div class="step"><div class="step-num">1</div><h3>Simule seu potencial</h3><p>Pela declaração completa do Imposto de Renda, você pode destinar até {{ lei.pctPF }}% do IRPF devido aos projetos parceiros da Abrace uma Causa. Use o nosso simulador para descobrir o seu potencial de doação.</p></div>
        <div class="step"><div class="step-num">2</div><h3>Faça sua doação</h3><p>Escolha o valor e faça seu pagamento com Pix, cartão de crédito ou boleto — de forma rápida e segura.</p></div>
        <div class="step"><div class="step-num">3</div><h3>Seu dinheiro de volta</h3><p>Enviaremos o recibo da sua doação e as instruções para declará-la no seu IRPF. Na declaração pré-preenchida, o valor aparece automaticamente no sistema da Receita Federal.</p></div>
      </div>
    </div>
  </section>

  <!-- SIMULADOR (seção própria, com espaçamento da seção acima) -->
  <section class="pad sim-section" id="simulador">
    <div class="wrap">
      <div class="section-head center">
        <h2>Descubra seu potencial de doação</h2>
        <p>Preencha seus dados e veja, em segundos, quanto do seu Imposto de Renda você pode destinar a este projeto — sem custo extra.</p>
      </div>
      <div class="sim-slot">
        <slot name="simulador">
          <a class="btn btn-lime" href="#doar" @click.prevent="emitDoar('Descobrir potencial - Simulador')">Descobrir potencial de doação</a>
        </slot>
      </div>
    </div>
  </section>

  <!-- BENEFÍCIOS DA PLATAFORMA (fixo) -->
  <section class="secure pad" id="seguranca">
    <div class="wrap">
      <div class="section-head center">
        <h2>Simples, rápido e seguro do clique ao recibo</h2>
        <p>A Abrace uma Causa fica do seu lado em toda a doação. Você cuida da boa ação; a gente cuida do resto.</p>
      </div>
      <div class="secure-grid">
        <div class="sfeat"><span class="ic"><i class="fal fa-laptop"></i></span><h3>100% online</h3><p>Faça tudo pelo celular ou computador, em poucos minutos, sem papelada e sem filas.</p></div>
        <div class="sfeat"><span class="ic"><i class="fal fa-bolt"></i></span><h3>Rápido e simples</h3><p>Pouquíssimos passos do início ao fim. A gente explica cada etapa de um jeito fácil de entender.</p></div>
        <div class="sfeat"><span class="ic"><i class="fal fa-shield-check"></i></span><h3>Seguro na sua declaração</h3><p>Cuidamos da gestão das informações para você abater a doação corretamente, com os documentos certos.</p></div>
        <div class="sfeat"><span class="ic"><i class="fal fa-credit-card"></i></span><h3>Pague como preferir</h3><p>Cartão de crédito, Pix e outros meios de pagamento — a doação na conveniência que funciona para você.</p></div>
        <div class="sfeat"><span class="ic"><i class="fal fa-file-invoice"></i></span><h3>Documentos fiscais eletrônicos</h3><p>Você recebe o recibo e o informe de doações prontinhos para usar na sua declaração.</p></div>
        <div class="sfeat"><span class="ic"><i class="fal fa-user-circle"></i></span><h3>Área exclusiva + suporte</h3><p>Acompanhe suas doações em uma área logada e conte com o suporte da AUC sempre que precisar.</p></div>
      </div>
    </div>
  </section>

  <!-- ODS -->
  <section class="pad-sm ods-sec" v-if="odsList.length">
    <div class="wrap">
      <div class="section-head center" style="margin-bottom:28px">
        <h2>Objetivos de Desenvolvimento Sustentável</h2>
        <p>Este projeto contribui para as seguintes metas globais da ONU.</p>
      </div>
      <div class="ods-strip">
        <div class="ods-card" v-for="o in odsList" :key="o.codigo" :style="{ background: o.cor }">
          <b>{{ o.num }}</b>
          <span>{{ o.nome }}</span>
        </div>
      </div>
    </div>
  </section>

  <!-- FAIXA DE MÉTRICAS -->
  <section class="metrics-band pad-sm" id="impacto" v-if="metricas.length">
    <div class="wrap">
      <div class="section-head center" style="margin-bottom:34px">
        <h2 style="color:#fff">Por que este projeto importa</h2>
      </div>
      <div class="metrics">
        <div class="metric" v-for="(m, i) in metricas" :key="i">
          <b :class="{ 'metric-sm': m.small }">{{ m.valor }}</b>
          <span>{{ m.label }}</span>
        </div>
      </div>
    </div>
  </section>

  <!-- SOBRE O PROJETO -->
  <section class="pad" id="sobre">
    <div class="wrap about-grid">
      <div class="about-text">
        <h2 style="font-size:clamp(1.9rem,3.6vw,2.6rem);margin-top:13px">{{ projeto.nome }}</h2>
        <div class="lead rich" v-html="projeto.objetivo"></div>
        <div class="about-just rich" v-if="projeto.justificativa" v-html="projeto.justificativa"></div>
        <div class="pull">
          <span class="pull-ic"><i class="fal fa-landmark"></i></span>
          <div>
            <b>{{ projeto.instituicao.razaoSocial }}</b>
            <div class="pull-desc rich" v-if="projeto.instituicao.descricao" v-html="projeto.instituicao.descricao"></div>
          </div>
        </div>
      </div>
      <div class="about-media">
        <span class="blob"></span>
        <div class="ph" v-if="projeto.galeria && projeto.galeria[0]">
          <img :src="projeto.galeria[0].url" :alt="projeto.galeria[0].legenda || projeto.nome">
        </div>
      </div>
    </div>
  </section>

  <!-- VÍDEO (esconde se vazio · player embutido) -->
  <section class="video-sec pad" v-if="projeto.videoUrl">
    <div class="wrap">
      <div class="section-head center"><h2>Conheça o projeto</h2></div>
      <div class="video-wrap">
        <div class="video-frame" v-if="!videoAtivo" @click="videoAtivo = true; ga('Play video')">
          <img v-if="youtubeId" class="video-thumb" :src="videoThumb" @error="onThumbError" alt="" aria-hidden="true">
          <div class="play"><i class="fas fa-play"></i></div>
          <div class="video-cap">Assista ao vídeo<span>Toque para reproduzir aqui</span></div>
        </div>
        <div class="video-embed" v-else>
          <iframe :src="videoEmbed" title="Vídeo do projeto" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
        </div>
      </div>
    </div>
  </section>

  <!-- GALERIA (esconde se vazia) -->
  <section class="pad" v-if="projeto.galeria && projeto.galeria.length">
    <div class="wrap">
      <div class="section-head"><h2>Imagens do projeto</h2></div>
      <div class="gallery">
        <div class="g has-img" :class="{ 'wide tall': i === 0 }" v-for="(g, i) in projeto.galeria" :key="i"
          @click="abrirGaleria(i)" title="Ampliar">
          <img :src="g.url" :alt="g.legenda || projeto.nome">
          <span class="g-zoom"><i class="fal fa-expand-alt"></i></span>
        </div>
      </div>
      <vue-easy-lightbox
        :visible="galleryVisible"
        :imgs="galeriaUrls"
        :index="galleryIndex"
        @hide="galleryVisible = false"
      ></vue-easy-lightbox>
    </div>
  </section>

  <!-- CTA FINAL -->
  <section class="final pad">
    <div class="wrap final-inner">
      <h2>Seu imposto pode virar transformação</h2>
      <p>Você já vai pagar esse imposto. Decida para onde ele vai: destine até {{ lei.pctPF }}% do seu Imposto de Renda para este projeto — sem custo extra.</p>
      <a class="btn btn-lime" href="#doar" @click.prevent="emitDoar('Quero doar agora - CTA final')">Quero doar agora</a>
    </div>
  </section>

  <!-- CONTATO (fixo) -->
  <section class="contact pad" id="contato">
    <div class="wrap contact-grid">
      <div class="form-card">
        <h2>Quer saber mais sobre o projeto?</h2>
        <div class="row2">
          <div class="field"><label>Nome</label><input type="text" placeholder="Seu nome completo"></div>
          <div class="field"><label>Telefone</label><input type="tel" placeholder="(00) 00000-0000"></div>
        </div>
        <div class="field"><label>E-mail</label><input type="email" placeholder="voce@email.com"></div>
        <div class="field"><label>Mensagem</label><textarea placeholder="Escreva a sua mensagem"></textarea></div>
        <button class="btn btn-primary" style="width:100%" @click="ga('Enviar mensagem - Contato')">Enviar mensagem</button>
      </div>
    </div>
  </section>

  <!-- O footer do sistema é renderizado pelo container (ProjectLP.vue), fora deste componente. -->

</div>
</template>

<script type="plain/text">
export default {
  name: "PaginaProjeto",
  props: {
    projeto: { type: Object, required: true },
    // gradiente/cor da campanha aplicado no hero (null = usa o gradiente padrão do CSS)
    heroStyle: { type: Object, default: null },
    // paleta da campanha (map de CSS custom properties: --teal, --lime, --mist...) aplicada
    // no root do componente. null = mantém os tokens padrão da marca AUC definidos no CSS.
    themeVars: { type: Object, default: null },
  },
  data() {
    return {
      videoAtivo: false,
      thumbRes: 0, // 0=hq, 1=mq (hqdefault existe p/ praticamente todo vídeo -> evita 404 do maxres)
      galleryVisible: false,
      galleryIndex: 0,
      // Percentual de dedução por lei. AUC confirmado: ECA/Idoso/Cultura = 6%, Esporte = 7%.
      LEIS: {
        FIA:        { rotulo: 'Lei da Criança e do Adolescente', fundo: 'Fundo da Infância e Adolescência', pctPF: 6, conselho: true },
        ECA:        { rotulo: 'Lei da Criança e do Adolescente', fundo: 'Fundo da Infância e Adolescência', pctPF: 6, conselho: true },
        IDOSO:      { rotulo: 'Estatuto do Idoso',               fundo: 'Fundo do Idoso',                    pctPF: 6, conselho: true },
        ESPORTE:    { rotulo: 'Lei de Incentivo ao Esporte',     fundo: 'projeto esportivo',                 pctPF: 7, conselho: false },
        CULTURA:    { rotulo: 'Lei Rouanet (PRONAC)',            fundo: 'projeto cultural',                  pctPF: 6, conselho: false },
      },
      ODS_META: {
        ODS1:  { cor: '#E5243B', nome: 'Erradicação da Pobreza' },
        ODS2:  { cor: '#DDA63A', nome: 'Fome Zero e Agricultura Sustentável' },
        ODS3:  { cor: '#4C9F38', nome: 'Saúde e Bem-Estar' },
        ODS4:  { cor: '#C5192D', nome: 'Educação de Qualidade' },
        ODS5:  { cor: '#FF3A21', nome: 'Igualdade de Gênero' },
        ODS6:  { cor: '#26BDE2', nome: 'Água Potável e Saneamento' },
        ODS7:  { cor: '#FCC30B', nome: 'Energia Limpa e Acessível' },
        ODS8:  { cor: '#A21942', nome: 'Trabalho Decente e Crescimento Econômico' },
        ODS9:  { cor: '#FD6925', nome: 'Indústria, Inovação e Infraestrutura' },
        ODS10: { cor: '#DD1367', nome: 'Redução das Desigualdades' },
        ODS11: { cor: '#FD9D24', nome: 'Cidades e Comunidades Sustentáveis' },
        ODS12: { cor: '#BF8B2E', nome: 'Consumo e Produção Responsáveis' },
        ODS13: { cor: '#3F7E44', nome: 'Ação Contra a Mudança Global do Clima' },
        ODS14: { cor: '#0A97D9', nome: 'Vida na Água' },
        ODS15: { cor: '#56C02B', nome: 'Vida Terrestre' },
        ODS16: { cor: '#00689D', nome: 'Paz, Justiça e Instituições Eficazes' },
        ODS17: { cor: '#19486A', nome: 'Parcerias e Meios de Implementação' },
      },
    };
  },
  mounted() {
    // page-view da página de projeto (Saiba Mais)
    this.ga('Visualizou pagina');
  },
  computed: {
    anoAtual() {
      return new Date().getFullYear();
    },
    lei() {
      return this.LEIS[this.projeto.leiIncentivo] || this.LEIS.FIA;
    },
    pctCaptacao() {
      const meta = Number(this.projeto.valorAprovado) || 0;
      const captado = Number(this.projeto.valorCaptado) || 0;
      if (!meta) return 0;
      return Math.min(100, Math.round((captado / meta) * 100));
    },
    local() {
      const c = this.projeto.cidades || [];
      const u = this.projeto.ufs || [];
      if (c.length) return c.slice(0, 2).join(' · ') + (u.length ? ' / ' + u[0] : '');
      if (u.length) return u.join(' · ');
      return '';
    },
    localMetric() {
      const c = this.projeto.cidades || [];
      const u = this.projeto.ufs || [];
      if (c.length > 1) return { valor: c.length, label: 'cidades atendidas' };
      if (c.length === 1) return { valor: c[0], label: 'onde acontece', small: true };
      if (u.length > 1) return { valor: u.length, label: 'estados atendidos' };
      if (u.length === 1) return { valor: u[0], label: 'onde acontece', small: true };
      return null;
    },
    metricas() {
      const p = this.projeto, m = [];
      if (p.qtdBeneficiarios)          m.push({ valor: this.fmtNum(p.qtdBeneficiarios), label: 'pessoas impactadas' });
      if (p.qtdBeneficiariosIndiretos) m.push({ valor: this.fmtNum(p.qtdBeneficiariosIndiretos), label: 'beneficiários indiretos' });
      if (this.localMetric)            m.push(this.localMetric);
      if (p.tagsPublico && p.tagsPublico.length) m.push({ valor: p.tagsPublico[0], label: 'público atendido', small: true });
      if (p.tagsCausa && p.tagsCausa.length)     m.push({ valor: p.tagsCausa[0], label: 'área de atuação', small: true });
      return m.slice(0, 4);
    },
    odsList() {
      return (this.projeto.tagsODS || [])
        .map((t) => {
          const num = String(t).replace(/\D/g, '');
          const meta = this.ODS_META['ODS' + num] || { cor: '#0B4F56', nome: t };
          return { codigo: t, num: num, cor: meta.cor, nome: meta.nome };
        })
        .filter((o) => o.num);
    },
    youtubeId() {
      const m = String(this.projeto.videoUrl || '').match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
      return m ? m[1] : '';
    },
    videoThumb() {
      if (!this.youtubeId) return '';
      var res = ['hqdefault', 'mqdefault'][this.thumbRes] || 'mqdefault';
      // i.ytimg.com é o domínio canônico (img.youtube.com só redireciona pra cá)
      return 'https://i.ytimg.com/vi/' + this.youtubeId + '/' + res + '.jpg';
    },
    videoEmbed() {
      return this.youtubeId ? 'https://www.youtube.com/embed/' + this.youtubeId + '?autoplay=1&rel=0' : this.projeto.videoUrl;
    },
    galeriaUrls() {
      return (this.projeto.galeria || []).map(function (g) { return g.url; });
    },
  },
  methods: {
    onThumbError() {
      // fallback raro: se hqdefault falhar, desce para mqdefault (sempre existe)
      if (this.thumbRes < 1) this.thumbRes++;
    },
    scrollToSimulador() {
      var el =
        (this.$el && this.$el.querySelector("#simulador")) ||
        document.getElementById("simulador");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    abrirGaleria(i) {
      this.galleryIndex = i;
      this.galleryVisible = true;
      this.ga('Abriu galeria', this.projeto.nome + ' #' + (i + 1));
    },
    // dispara evento GA (o projeto já inicializa o gtag; aqui é só o disparo).
    // categoria fixa "Saiba Mais"; label = nome do projeto por padrão (atribuição).
    ga(action, label) {
      if (typeof this.$gtag === 'function') {
        this.$gtag('send', 'event', 'Saiba Mais', action, label || (this.projeto && this.projeto.nome));
      }
    },
    // trackeia e dispara o fluxo de doação (o container ouve @doar)
    emitDoar(origem) {
      this.ga(origem);
      this.$emit('doar');
    },
    fmtNum(n) { return Number(n || 0).toLocaleString('pt-BR'); },
    brl(n) { return Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); },
    brlCompacto(n) {
      const v = Number(n || 0);
      if (v >= 1e6) return 'R$ ' + (v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' mi';
      if (v >= 1e3) return 'R$ ' + (v / 1e3).toLocaleString('pt-BR', { maximumFractionDigits: 0 }) + ' mil';
      return this.brl(v);
    },
  },
};
</script>

<style scoped>
/* ====== Tokens da marca AUC ====== */
.pagina-projeto{
  --teal:#009CA6;--teal-700:#017C84;--teal-900:#0B4F56;--teal-ink:#08373C;
  --lime:#8CC63F;--lime-700:#6FA82E;
  --ink:#13323A;--ink-soft:#4a626a;--ink-faint:#7d949b;
  --paper:#fff;--sand:#F4F9F9;--mist:#E7F4F4;--mist-2:#D6EBEC;
  --line:rgba(19,50,58,.10);--line-2:rgba(19,50,58,.16);
  --shadow-sm:0 1px 2px rgba(8,55,60,.06),0 6px 16px rgba(8,55,60,.07);
  --shadow-md:0 14px 34px rgba(8,55,60,.12),0 3px 10px rgba(8,55,60,.06);
  --shadow-lg:0 34px 80px rgba(8,55,60,.22);
  --r-sm:12px;--r-md:18px;--r-lg:26px;--r-xl:38px;--maxw:1180px;
  font-family:"Mulish",system-ui,sans-serif;color:var(--ink);background:var(--sand);line-height:1.6;
  -webkit-font-smoothing:antialiased;overflow-x:hidden;
  padding-top:70px; /* compensa a Nav fixa do sistema (.nav-bar: position:fixed; height:70px) */
}
.pagina-projeto *{box-sizing:border-box;margin:0;padding:0}
/* color:var(--ink) explícito: impede que um h2/h3 global do sistema (#02ACB4) vaze
   por herança nos títulos de seção — os títulos brancos são reafirmados adiante. */
.pagina-projeto h1,.pagina-projeto h2,.pagina-projeto h3{font-family:"Poppins",sans-serif;font-weight:700;line-height:1.12;letter-spacing:-.015em;color:var(--ink)}
.wrap{max-width:var(--maxw);margin:0 auto;padding:0 24px}
.eyebrow{font-size:.74rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:var(--teal);display:inline-flex;align-items:center;gap:9px}
.eyebrow::before{content:"";width:24px;height:3px;background:var(--lime);border-radius:3px}
a{color:inherit;text-decoration:none}

.btn{display:inline-flex;align-items:center;justify-content:center;gap:10px;font-weight:800;font-size:1rem;padding:15px 28px;border-radius:999px;border:2px solid transparent;cursor:pointer;transition:transform .25s,box-shadow .25s,background .2s;white-space:nowrap}
.btn-primary{background:var(--teal);color:#fff;box-shadow:0 10px 24px rgba(0,156,166,.30)}
.btn-primary:hover{background:var(--teal-700);transform:translateY(-2px)}
.btn-lime{background:var(--lime);color:var(--lime-ink,#1f3a08);box-shadow:0 10px 24px rgba(140,198,63,.35)}
.btn-lime:hover{background:var(--lime-700);color:#fff;transform:translateY(-2px)}
.btn-soft{background:var(--mist);color:var(--teal-700)}
.btn-soft:hover{background:var(--mist-2)}
.btn-sm{padding:11px 20px;font-size:.92rem}

.hero{position:relative;background:linear-gradient(135deg,#0B4F56 0%,#08373C 60%,#062b30 100%);overflow:hidden}
.hero::after{content:"";position:absolute;width:520px;height:520px;border-radius:50%;right:-160px;top:-200px;background:radial-gradient(circle,rgba(140,198,63,.30),transparent 65%)}
.hero-inner{position:relative;display:grid;grid-template-columns:1.05fr .95fr;gap:50px;align-items:center;padding:64px 0 132px;color:#fff;z-index:1}
.hero-tag{display:inline-flex;align-items:center;gap:9px;font-size:.74rem;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:#fff;background:rgba(255,255,255,.14);border:1px solid rgba(255,255,255,.35);padding:7px 15px;border-radius:999px}
.hero h1{font-size:clamp(2.2rem,4.8vw,3.7rem);margin:22px 0 16px;color:#fff}
.hero-sub{font-size:1.12rem;color:rgba(255,255,255,.84);max-width:42ch}
/* texto rico vindo do banco (minibio/objetivo/justificativa) renderizado via v-html;
   o reset .pagina-projeto * zera margens, entao restauramos espacamento de paragrafos */
.rich p{margin-bottom:.6em}
.rich p:last-child{margin-bottom:0}
.rich strong{font-weight:700}
.about-text .about-just{color:var(--ink-soft);font-size:1.06rem;margin-bottom:16px}
.pull .pull-desc{margin:0;font-size:.93rem;color:var(--ink-soft)}
.hero-chips{display:flex;flex-wrap:wrap;gap:10px;margin:24px 0 30px}
.chip{display:inline-flex;align-items:center;gap:8px;font-size:.85rem;font-weight:700;background:rgba(255,255,255,.10);border:1px solid rgba(255,255,255,.2);padding:8px 14px;border-radius:999px;color:rgba(255,255,255,.94)}
.hero-cta{display:flex;flex-wrap:wrap;gap:14px}
/* ===== Hero dinâmico (contraste garantido em qualquer campanha) =====
   - texto do hero (incl. o rich vindo do CMS, que pode trazer cor inline escura) é
     forçado para --hero-ink, que contrasta com o gradiente da primária;
   - o CTA vira um bloco na cor --hero-ink com label na cor oposta (--hero-cta-fg),
     destacando-se do fundo mesmo quando a secundária é próxima da primária. */
.hero h1,.hero .hero-sub,.hero-tag{color:var(--hero-ink,#fff)!important}
/* /deep/ para alcançar o conteúdo injetado via v-html (o CSS scoped não marca esses
   nós), sobrepondo cores inline escuras que o CMS grava na descrição */
.hero .hero-sub.rich /deep/ *{color:var(--hero-ink,#fff)!important}
.hero .hero-cta .btn-lime{background:var(--hero-ink,#fff);color:var(--hero-cta-fg,#08373C);box-shadow:0 12px 28px rgba(0,0,0,.22)}
.hero .hero-cta .btn-lime:hover{background:var(--hero-ink,#fff);color:var(--hero-cta-fg,#08373C);transform:translateY(-2px);filter:brightness(.96)}
.hero-photo{position:relative}
.hero-photo .frame{position:relative;border-radius:var(--r-xl);overflow:hidden;box-shadow:var(--shadow-lg);aspect-ratio:1/1;border:5px solid rgba(255,255,255,.12)}
.hero-photo .frame img{width:100%;height:100%;object-fit:cover;display:block}
.hero-photo .blob{position:absolute;border-radius:50%;z-index:-1}
/* classe "accentblob" (não "lime") para não colidir com a utilitária .lime do Vuetify (!important) */
.hero-photo .blob.accentblob{width:120px;height:120px;background:var(--accent-fill,var(--lime));left:-34px;bottom:40px;opacity:.9}
.hero-photo .blob.ring{width:90px;height:90px;border:14px solid rgba(255,255,255,.18);right:-28px;top:-20px}
.hero-photo .floattag{position:absolute;left:-22px;top:34px;background:#fff;color:var(--ink);border-radius:16px;padding:12px 16px;box-shadow:var(--shadow-md);display:flex;align-items:center;gap:10px}
.hero-photo .floattag .ic{width:36px;height:36px;border-radius:10px;background:var(--mist);display:grid;place-items:center;color:var(--teal);font-size:1.1rem}
.hero-photo .floattag b{font-family:"Poppins";font-size:.95rem;display:block;line-height:1.1}
.hero-photo .floattag span{font-size:.74rem;color:var(--ink-faint)}
@media(max-width:880px){.hero-inner{grid-template-columns:1fr;gap:38px;padding:48px 0 120px}.hero-sub{max-width:none}.hero-photo .frame{aspect-ratio:4/3}}

.curve{position:relative;height:0;z-index:2}
.curve svg{position:absolute;bottom:-1px;left:0;width:100%;height:80px;display:block}
/* a curva fecha o hero contra o fundo da página: usa --sand (segue a cor da campanha),
   sobrescrevendo o fill fixo do <path> */
.curve svg path{fill:var(--sand)}

.donate-wrap{position:relative;margin-top:-92px;z-index:5}
.donate-card{background:#fff;border-radius:var(--r-lg);padding:28px 30px;box-shadow:var(--shadow-lg);display:grid;grid-template-columns:1.3fr 1fr;gap:34px;align-items:center;border:1px solid var(--line)}
.dc-status{display:inline-flex;align-items:center;gap:8px;font-size:.78rem;font-weight:800;color:var(--teal-700);background:var(--mist);padding:6px 13px;border-radius:999px}
.dc-status .dot{width:8px;height:8px;border-radius:50%;background:var(--accent-fill,var(--lime));box-shadow:0 0 0 4px rgba(0,0,0,.06)}
.dc-raised{font-family:"Poppins";font-size:2.4rem;font-weight:700;margin:14px 0 2px;color:var(--ink)}
.dc-goal{font-size:.95rem;color:var(--ink-soft)}.dc-goal b{color:var(--ink)}
.progress{height:12px;border-radius:999px;background:var(--mist-2);margin:16px 0 9px;overflow:hidden}
.progress>i{display:block;height:100%;border-radius:999px;background:linear-gradient(90deg,var(--teal),var(--accent-fill,var(--lime)));transition:width 1.6s cubic-bezier(.2,.8,.2,1)}
.dc-meta{display:flex;justify-content:space-between;font-size:.82rem;color:var(--ink-faint);font-weight:700}
.dc-right{border-left:1px solid var(--line);padding-left:34px;display:flex;flex-direction:column;gap:11px}
.dc-highlight{background:var(--mist);border:1px solid var(--mist-2);border-radius:var(--r-md);padding:16px 18px;text-align:center;margin-bottom:4px}
.dc-highlight b{font-family:"Poppins";font-size:2.1rem;color:var(--teal-700);display:block;line-height:1}
.dc-highlight span{font-size:.84rem;color:var(--ink-soft);font-weight:700;display:block;margin-top:6px;line-height:1.3}
.dc-note{font-size:.76rem;color:var(--ink-faint);text-align:center}
@media(max-width:760px){.donate-card{grid-template-columns:1fr;gap:22px}.dc-right{border-left:0;border-top:1px solid var(--line);padding-left:0;padding-top:20px}}

section{position:relative}
.pad{padding:84px 0}.pad-sm{padding:58px 0}
.section-head{max-width:620px;margin-bottom:46px}
.section-head.center{margin-inline:auto;text-align:center}
.section-head h2{font-size:clamp(1.9rem,3.6vw,2.7rem);margin:13px 0 14px}
.section-head p{color:var(--ink-soft);font-size:1.06rem}

.steps{display:grid;grid-template-columns:repeat(3,1fr);gap:22px}
/* Neutraliza a linha de stepper global (.steps:before do _steps.styl) que vaza por colisão de nome */
.pagina-projeto .steps::before{content:none!important;display:none!important}
.step{background:#fff;border:1px solid var(--line);border-radius:var(--r-md);padding:30px 26px;transition:transform .3s,box-shadow .3s}
.step:hover{transform:translateY(-5px);box-shadow:var(--shadow-md)}
.step-num{font-family:"Poppins";font-size:1.05rem;font-weight:800;color:#fff;width:44px;height:44px;border-radius:13px;display:grid;place-items:center;margin-bottom:18px;background:var(--teal)}
/* badges dos passos em progressão de tons da PRIMÁRIA (não dependem da secundária,
   que pode ser preto/branco/próxima da primária) — texto branco em todos */
.step:nth-child(2) .step-num{background:var(--teal-700)}
.step:nth-child(3) .step-num{background:var(--teal-900)}
.step h3{font-size:1.2rem;margin-bottom:8px}
.step p{color:var(--ink-soft);font-size:.96rem}
@media(max-width:760px){.steps{grid-template-columns:1fr}}

.secure{background:var(--mist)}
.secure-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
.sfeat{background:#fff;border:1px solid var(--line);border-radius:var(--r-md);padding:26px 24px;transition:transform .3s,box-shadow .3s}
.sfeat:hover{transform:translateY(-4px);box-shadow:var(--shadow-md)}
.sfeat .ic{width:48px;height:48px;border-radius:13px;background:var(--mist);display:grid;place-items:center;font-size:1.4rem;margin-bottom:15px}
.sfeat h3{font-size:1.06rem;margin-bottom:6px}
.sfeat p{color:var(--ink-soft);font-size:.93rem}
@media(max-width:820px){.secure-grid{grid-template-columns:1fr 1fr}}
@media(max-width:560px){.secure-grid{grid-template-columns:1fr}}

.ods-strip{display:flex;flex-wrap:wrap;justify-content:center;gap:14px}
.ods-card{width:172px;border-radius:var(--r-md);padding:18px 18px 16px;color:#fff;min-height:118px;display:flex;flex-direction:column;justify-content:space-between;box-shadow:var(--shadow-sm)}
.ods-card b{font-family:"Poppins";font-size:1.9rem;line-height:1}
.ods-card span{font-size:.8rem;font-weight:700;line-height:1.2}

.metrics-band{background:var(--teal-900);color:#fff;overflow:hidden}
.metrics{display:flex;flex-wrap:wrap;justify-content:center;align-items:flex-start;gap:34px 52px;text-align:center}
.metric{flex:0 1 200px;min-width:150px;display:flex;flex-direction:column;align-items:center}
/* caixa de altura fixa com o valor alinhado à base: números grandes e textos de 1-2
   linhas ficam sobre a mesma linha, mantendo todos os labels alinhados */
.metric b{font-family:"Poppins";font-size:clamp(2rem,4vw,2.9rem);font-weight:700;line-height:1;color:var(--metric-accent,var(--lime));min-height:3.4rem;display:flex;align-items:flex-end;justify-content:center;text-align:center;margin-bottom:8px}
.metric b.metric-sm{font-size:clamp(1.05rem,1.9vw,1.4rem);line-height:1.18}
.metric span{font-size:.9rem;color:rgba(255,255,255,.85);font-weight:600}

.about-grid{display:grid;grid-template-columns:1fr 1fr;gap:56px;align-items:center}
.about-text .lead{font-size:1.22rem;color:var(--ink);font-weight:700;font-family:"Poppins";line-height:1.4;margin:14px 0 18px}
.about-text p{color:var(--ink-soft);font-size:1.06rem;margin-bottom:16px}
.pull{display:flex;gap:15px;margin-top:24px;background:var(--mist);border-radius:var(--r-md);padding:22px;border:1px solid var(--mist-2)}
.pull .pull-ic{font-size:1.8rem;flex:none}
.pull b{display:block;font-family:"Poppins";font-size:1.02rem;margin-bottom:2px}
.pull p{margin:0;font-size:.93rem;color:var(--ink-soft)}
.about-media{position:relative}
.about-media .ph{position:relative;border-radius:var(--r-lg);overflow:hidden;border:1px solid var(--line);min-height:330px;background:var(--mist)}
.about-media .ph img{width:100%;height:100%;object-fit:cover;display:block;min-height:330px}
.about-media .blob{position:absolute;width:120px;height:120px;border-radius:50%;background:var(--accent-fill,var(--lime));right:-30px;top:-30px;z-index:-1;opacity:.85}
@media(max-width:860px){.about-grid{grid-template-columns:1fr;gap:40px}}

.video-sec{background:var(--mist)}
.video-wrap{max-width:880px;margin:0 auto}
.video-frame{position:relative;border-radius:var(--r-lg);overflow:hidden;box-shadow:var(--shadow-md);aspect-ratio:16/9;background:linear-gradient(135deg,var(--teal-900),#062b30);background-size:cover;background-position:center;display:grid;place-items:center;cursor:pointer}
.video-frame::before{content:"";position:absolute;inset:0;background:rgba(8,55,60,.35);z-index:1}
.video-frame .video-thumb{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:0;display:block}
.play{position:relative;z-index:1;width:84px;height:84px;border-radius:50%;background:var(--lime);display:grid;place-items:center;font-size:1.8rem;color:#1f3a08;box-shadow:0 14px 40px rgba(140,198,63,.5);transition:transform .3s}
.video-frame:hover .play{transform:scale(1.08)}
.video-cap{position:absolute;z-index:1;bottom:22px;left:24px;color:#fff;font-weight:700}
.video-cap span{display:block;font-size:.8rem;color:rgba(255,255,255,.78);font-weight:500}
.video-embed{position:relative;aspect-ratio:16/9;border-radius:var(--r-lg);overflow:hidden;box-shadow:var(--shadow-md)}
.video-embed iframe{position:absolute;inset:0;width:100%;height:100%;border:0}
.sim-slot{margin-top:30px;display:flex;justify-content:center}
.sim-section{scroll-margin-top:90px;background:var(--teal-900)}
.sim-section .section-head h2{color:#fff}
.sim-section .section-head p{color:rgba(255,255,255,.82)}
.sim-section .eyebrow{color:var(--lime-bright,var(--lime))}
/* barra do eyebrow também clareada nas faixas escuras (senão fica dim sobre a primária) */
.metrics-band .eyebrow::before,.sim-section .eyebrow::before{background:var(--lime-bright,var(--lime))}

.gallery{display:grid;grid-template-columns:repeat(4,1fr);grid-auto-rows:180px;gap:14px}
.g{border-radius:var(--r-md);overflow:hidden;position:relative;border:1px solid var(--line);background:var(--mist)}
.g.has-img{cursor:zoom-in}
.g.has-img img{width:100%;height:100%;object-fit:cover;display:block;position:absolute;inset:0;transition:transform .35s}
.g.has-img:hover img{transform:scale(1.05)}
.g-zoom{position:absolute;right:10px;bottom:10px;z-index:1;width:32px;height:32px;border-radius:9px;background:rgba(8,55,60,.55);color:#fff;display:grid;place-items:center;font-size:15px;opacity:0;transition:opacity .25s}
.g.has-img:hover .g-zoom{opacity:1}
.g.tall{grid-row:span 2}.g.wide{grid-column:span 2}
@media(max-width:760px){.gallery{grid-template-columns:1fr 1fr;grid-auto-rows:140px}.g.tall{grid-row:span 1}}

.final{background:linear-gradient(135deg,var(--teal),var(--teal-900));color:#fff;text-align:center;overflow:hidden}
.final h2{color:#fff;font-size:clamp(2rem,4vw,3rem);margin-bottom:16px}
.final p{color:rgba(255,255,255,.9);font-size:1.12rem;max-width:54ch;margin:0 auto 30px}
/* CTA final sobre o gradiente escuro da primária: mesmo tratamento do hero (bloco
   --hero-ink + label --hero-cta-fg), destaca em qualquer campanha (sem virar preto) */
.final .btn-lime{font-size:1.08rem;padding:17px 38px;background:var(--hero-ink,#fff);color:var(--hero-cta-fg,#08373C);box-shadow:0 12px 28px rgba(0,0,0,.22)}
.final .btn-lime:hover{background:var(--hero-ink,#fff);color:var(--hero-cta-fg,#08373C);transform:translateY(-2px);filter:brightness(.96)}

.contact{background:var(--sand)}
.contact-grid{max-width:680px;margin:0 auto}
.form-card{background:#fff;border-radius:var(--r-lg);padding:34px;box-shadow:var(--shadow-md);border:1px solid var(--line)}
.form-card h2{font-size:1.7rem;margin:8px 0 22px}
.field{margin-bottom:16px}
.field label{display:block;font-size:.8rem;font-weight:800;color:var(--ink-soft);margin-bottom:6px}
.field input,.field textarea{width:100%;padding:13px 16px;border:1px solid var(--line-2);border-radius:12px;font-size:1rem;color:var(--ink);background:var(--sand)}
.field input:focus,.field textarea:focus{outline:0;border-color:var(--teal);box-shadow:0 0 0 4px rgba(0,156,166,.13);background:#fff}
.field textarea{resize:vertical;min-height:96px}
.row2{display:grid;grid-template-columns:1fr 1fr;gap:16px}
@media(max-width:540px){.row2{grid-template-columns:1fr}}

.logo-card{position:absolute;left:-16px;bottom:22px;background:#fff;border-radius:16px;padding:11px 16px 9px;box-shadow:var(--shadow-md);text-align:center;max-width:166px}
.logo-card .lc-label{display:block;font-size:.6rem;font-weight:800;letter-spacing:.16em;text-transform:uppercase;color:var(--ink-faint);margin-bottom:6px}
.logo-card img{max-width:122px;max-height:58px;width:auto;height:auto;display:block;margin:0 auto}
@media(max-width:880px){.logo-card{left:auto;right:14px;bottom:14px}}

/* ===== Ajustes mobile (evita vazamento horizontal) ===== */
@media(max-width:600px){
  /* no mobile o logo da campanha pode exceder os 70px da nav fixa; folga extra no topo */
  .pagina-projeto{padding-top:100px}
  .wrap{padding:0 16px}
  /* botões longos (ex: "Doar com meu Imposto de Renda") precisam quebrar em vez de empurrar a largura */
  .btn{white-space:normal;text-align:center;padding:13px 18px;font-size:.95rem}
  .hero h1{font-size:1.9rem}
  .hero-sub{font-size:1rem}
  .hero-tag{margin-top:28px}
  .hero-inner{padding-bottom:72px}
  .hero-photo .floattag{left:0;top:14px}
  /* no mobile o card de doação fica ABAIXO do hero (sem sobrepor a foto/logo) */
  .donate-wrap{margin-top:24px}
  .donate-card{padding:20px 16px}
  .dc-raised{font-size:1.9rem}
  .dc-right{padding-top:18px}
  .dc-highlight b{font-size:1.7rem}
  .dc-meta{font-size:.72rem;gap:10px}
  .section-head h2,.final h2{font-size:1.6rem}
  .metric{flex:0 1 45%;min-width:0}
}
</style>
